import React, {useEffect, useState} from 'react';

import isEqual from 'lodash/isEqual';

import Autocomplete, {AutoCompleteBaseProps} from '@components/auto-complete-base';

const DEF_GET_OPTION_VALUE = option => option.value;
const DEF_OPTIONS = [];

interface Props<T = {title: string; value: number | string}, V = string>
  extends Omit<AutoCompleteBaseProps<T>, 'value'> {
  value: V;
}

/**
 * AutoComplete with Auto-suggest and ability to create additional options on the fly
 *
 * Simplest usage is:
 *
 * import React, {useState} from 'react';
 * import AutoComplete from '@components/auto-complete';
 *
 * const options = [
 *   {title: 'First Title', value: '1'},
 *   {title: 'Second Title', value: '2'},
 * ];
 *
 * const MyComponent = () => {
 *   const [value, setValue] = useState();
 *
 *   return (
 *     <AutoComplete
 *        options={options}
 *        value={value}
 *        onChange={setValue}
 *     />
 *   );
 * }
 */
export const AutoComplete = <T, V>({
  value,
  options = DEF_OPTIONS,
  getValue = DEF_GET_OPTION_VALUE,
  ...rest
}: Props<T, V>) => {
  const [preparedValue, setPreparedValue] = useState<T | null>(null);
  useEffect(() => {
    let newPreparedValue: T | undefined = undefined;
    if (value) {
      newPreparedValue = (options as T[]).find(option => {
        return isEqual(getValue(option), value);
      });
    }
    if (!isEqual(newPreparedValue, preparedValue)) {
      setPreparedValue(newPreparedValue ?? null);
    }
  }, [getValue, options, preparedValue, value]);

  return (
    <Autocomplete<T, false>
      {...rest}
      autoHighlight
      multiple={false}
      value={preparedValue}
      options={options}
      getValue={getValue}
    />
  );
};
